import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import Unicon from '../Unicon';
import EmptyListPlaceholder from '../EmptyListPlaceholder';
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SheetModal from '../SheetModal';
import { StyleSheet } from 'react-native-unistyles';

const SortableItemComponent = ({ item, index, itemContent, itemHeight, draggingItem, getItemStyle }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const extraStyle = getItemStyle ? getItemStyle(item, index) : {};

    return (
        <View style={[styles.sortableItem(item.id, draggingItem, itemHeight, style), extraStyle]} ref={setNodeRef}>
            <View style={styles.sortableItemContent}>
                <View style={{ cursor: draggingItem === item.id ? "grabbing" : "grab" }} {...attributes} {...listeners}>
                    <View style={{ marginRight: 10, opacity: 0.5 }}>
                        <Unicon name="reorder-four-outline" size={20} colorVariant="textSecondary" />
                    </View>
                </View>
                <View style={{ flexGrow: 1 }}>
                    {itemContent(item)}
                </View>
            </View>
        </View>
    );
};

const SortableList = ({items, itemContent, isOpen, close, onConfirm, title, itemHeight = 50, labels = [], helpText = "", footer = null, getItemStyle = null}) => {
    const [reorderedItems, setReorderedItems] = useState([]);
    const [draggingItem, setDraggingItem] = useState(null);

    useEffect(() => {
        if(items){
            setReorderedItems(items);
        }
    }, [items]);


    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
          coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        setDraggingItem(null)

        const {active, over} = event;
        
        if (active.id !== over.id) {
          setReorderedItems((items) => {
            const oldIndex = items.findIndex(item => item.id == active.id);
            const newIndex = items.findIndex(item => item.id == over.id);
            return arrayMove(items, oldIndex, newIndex);
          });
        }
    };


    return (
        <SheetModal
            helpText={helpText}
            title={title}
            cancelText="Cancel"
            confirmText="Confirm"
            onCancel={() => {
                close()
            }}
            onConfirm={() => {
                onConfirm(reorderedItems)
                close()
            }}
            isOpen={isOpen}
        >
            {reorderedItems?.length == 0 && <EmptyListPlaceholder spacing={10} message='No items to sort!'/>}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(event) => setDraggingItem(event.active.id)}
                onDragEnd={handleDragEnd}
                onDragOver={(event) => setDraggingItem(event.active.id)}
                onDragLeave={() => setDraggingItem(null)}
            >
                <SortableContext
                    items={reorderedItems}
                    strategy={verticalListSortingStrategy}
                >
                    <View style={{flexDirection:"row", alignItems:"center", justifyContent:"center"}}>
                        {labels.length > 0 &&
                            <View>
                                {labels.map((label, i) => <View style={{height:itemHeight+4, justifyContent:"center", paddingRight:10}} key={i}>
                                    {label}
                                </View>)}
                            </View>
                        }
                        <View style={{flex: 1}}>
                            {reorderedItems.map((item, index) => (
                                <SortableItemComponent
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    itemContent={itemContent}
                                    itemHeight={itemHeight}
                                    draggingItem={draggingItem}
                                    getItemStyle={getItemStyle}
                                />
                            ))}
                        </View>
                    </View>
                </SortableContext>
            </DndContext>
            {footer}
        </SheetModal>
    );
}

const styles = StyleSheet.create(({colors}) => ({
    sortableItem: (hasItemID, draggingItem, itemHeight, style) => ({
        boxShadow:draggingItem == hasItemID ? "0px 0px 5px 2px rgba(0, 0, 0, 0.1)" : "none", 
        zIndex:draggingItem == hasItemID ? 1 : 0, 
        backgroundColor:colors.surface,
        padding:10,
        marginVertical:2,
        justifyContent:"center", 
        height:itemHeight, 
        ...style
    }),
    sortableItemContent: {
        flexDirection:"row", 
        alignItems:"center", 
        justifyContent:"center"
    }
}))

export default SortableList;
